import { AppNS } from 'app';
import { apiGetProjectManagers } from 'app/api/users';
import { useAuthStore } from 'app/core/store_v2/auth';
import { UserData } from 'app/types';
import axios from 'axios';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { signOut } from '../services/jwtService';
import { AppThunk } from './';
import { AVAILABILITY_REDUCERS } from './auth/reducers/availability';
import { AVATAR_REDUCERS } from './auth/reducers/avatar';
import { DESCRIPTION_DATA_REDUCERS } from './auth/reducers/descriptionData';
import { LOCATIONS_REDUCERS } from './auth/reducers/locations';
import { NEEDS_REDUCERS } from './auth/reducers/needs';
import { OFFICE_CONTACT_DATA_REDUCERS } from './auth/reducers/officeContactData';
import { ONBOARDING_STEP_REDUCERS } from './auth/reducers/onboardingStep';
import { PERSONAL_DETAILS_REDUCERS } from './auth/reducers/personalDetails';
import { PUBLIC_DETAILS_DATA_REDUCERS } from './auth/reducers/publicDetailsData';
import { SETTINGS_REDUCERS } from './auth/reducers/settings';
import { UNAVAILABLE_PERIODS_REDUCERS } from './auth/reducers/unavailablePeriods';
import { VIDEO_REDUCERS } from './auth/reducers/video';

const cleanLocalStorage = () => {
  localStorage.removeItem('dcra_step1_success');
  localStorage.removeItem('dcra_client_register_success');
};

export interface AuthState {
  user: AppNS.User | null;
  projectManagers: Array<AppNS.User> | null;
  isLoadingProjectManagers: boolean;
  isChangingAddress: boolean;
  isChangingAddressError: boolean;
  isChangingAvailability: boolean;
  isChangingAvailabilityError: boolean;
  isChangingAvatar: boolean;
  isChangingAvatarError: boolean;
  isChangingVideo: boolean;
  isChangingVideoError: boolean;
  isChangingCurrentDistance: boolean;
  isChangingCurrentDistanceError: boolean;
  isChangingCurrentServiceAddressId: boolean;
  isChangingCurrentServiceAddressIdError: boolean;
  isChangingInterests: boolean;
  isChangingInterestsError: boolean;
  isChangingLanguages: boolean;
  isChangingLanguagesError: boolean;
  isChangingNeeds: boolean;
  isChangingNeedsError: boolean;
  isChangingPeriods: boolean;
  isChangingPeriodsError: boolean;
  isChangingPersonalData: boolean;
  isChangingPersonalDataError: boolean;
  isChangingStep: boolean;
  isChangingStepError: boolean;
  isLoggingOut: boolean;
  isChangingDescriptionData: string;
  isChangingDescriptionDataError: boolean;
  isLoadingCreditCard: boolean;
  isLoadingBankAccountSuccess: boolean;
  isLoadingCreditCardSuccess: boolean;
  isLoadingCreditCardError: boolean;
  isAddingCreditCard: boolean;
  isAddingCreditCardError: boolean;
  isRemovingCreditCard: boolean;
  removingCreditCardError: AppNS.Params | null;
  isChangingEmail: boolean;
  isChangingEmailError: boolean;
  isChangingEmailSuccess: boolean;
  isChangingMobile: boolean;
  isChangingMobileError: boolean;
  isChangingMobileSuccess: boolean;
  isChangingPublicDetailsData: boolean;
  isChangingPublicDetailsDataError: boolean;
  isChangingOfficeContactData: boolean;
  isChangingOfficeContactDataError: boolean;
  isLoadingBankAccount: boolean;
  isLoadingBankAccountError: boolean;
  isAddingBankAccount: boolean;
  isAddingBankAccountError: boolean;
  isRemovingBankAccount: boolean;
  removingBankAccountError: AppNS.Params | null;
  isChangingProposalPricing: boolean;
  isChangingProposalPricingError: boolean;
  isChangingFixedPricing: boolean;
  isChangingFixedPricingError: boolean;
  currentApp: string;
}

const initialState: AuthState = {
  user: null,
  projectManagers: [],
  isLoadingProjectManagers: false,
  isChangingAddress: false,
  isChangingAddressError: false,
  isChangingAvailability: false,
  isChangingAvailabilityError: false,
  isChangingAvatar: false,
  isChangingAvatarError: false,
  isChangingVideo: false,
  isChangingVideoError: false,
  isChangingCurrentDistance: false,
  isChangingCurrentDistanceError: false,
  isChangingCurrentServiceAddressId: false,
  isChangingCurrentServiceAddressIdError: false,
  isChangingInterests: false,
  isChangingInterestsError: false,
  isChangingLanguages: false,
  isChangingLanguagesError: false,
  isChangingNeeds: false,
  isChangingNeedsError: false,
  isChangingPeriods: false,
  isChangingPeriodsError: false,
  isChangingPersonalData: false,
  isChangingPersonalDataError: false,
  isChangingStep: false,
  isChangingStepError: false,
  isLoggingOut: false,
  isChangingDescriptionData: '',
  isChangingDescriptionDataError: false,
  isLoadingCreditCard: false,
  isLoadingCreditCardError: false,
  isAddingCreditCard: false,
  isAddingCreditCardError: false,
  isRemovingCreditCard: false,
  removingCreditCardError: null,
  isChangingEmail: false,
  isChangingEmailError: false,
  isChangingEmailSuccess: false,
  isChangingMobile: false,
  isChangingMobileError: false,
  isChangingMobileSuccess: false,
  isChangingPublicDetailsData: false,
  isChangingPublicDetailsDataError: false,
  isChangingOfficeContactData: false,
  isChangingOfficeContactDataError: false,
  isLoadingBankAccount: false,
  isLoadingBankAccountError: false,
  isAddingBankAccount: false,
  isAddingBankAccountError: false,
  isLoadingBankAccountSuccess: false,
  isLoadingCreditCardSuccess: false,
  isRemovingBankAccount: false,
  removingBankAccountError: null,
  isChangingProposalPricing: false,
  isChangingProposalPricingError: false,
  isChangingFixedPricing: false,
  isChangingFixedPricingError: false,
  currentApp: localStorage.getItem('currentApp') || 'third-party',
};

const getRole = (roles: AppNS.Role[]): AppNS.Role => {
  if (roles.length === 1) {
    return roles[0];
  }
  if (roles.includes('agency')) {
    return 'agency';
  } else if (roles.includes('backoffice')) {
    return 'backoffice';
  } else {
    return roles[0];
  }
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    ...AVAILABILITY_REDUCERS,
    ...AVATAR_REDUCERS,
    ...VIDEO_REDUCERS,
    ...LOCATIONS_REDUCERS,
    ...NEEDS_REDUCERS,
    ...ONBOARDING_STEP_REDUCERS,
    ...UNAVAILABLE_PERIODS_REDUCERS,
    ...PERSONAL_DETAILS_REDUCERS,
    ...DESCRIPTION_DATA_REDUCERS,
    ...SETTINGS_REDUCERS,
    ...PUBLIC_DETAILS_DATA_REDUCERS,
    ...OFFICE_CONTACT_DATA_REDUCERS,
    setAuthData(state: AuthState, action: { payload: AppNS.User }) {
      state.user = action.payload;
      state.user.role = getRole(action.payload.roles);

      // Connection with APP_V2 store (ELEVATORS)
      useAuthStore.setState({
        currentUser: action.payload as unknown as UserData,
      });

      if (!localStorage.getItem('currentApp')) {
        if (state.user.roles.includes('inspector')) {
          state.currentApp = 'dcra-inspections';
          localStorage.setItem('currentApp', 'dcra-inspections');
        }
      }
      if (state.user.employments && state.user.employments[0]) {
        state.user.company = state.user.employments[0].company;
      }

      if (state.user.onboardingStep || state.user.roles.includes('client')) {
        cleanLocalStorage();
      }
    },
    updateUserData(state: AuthState, action: { payload: AppNS.Params }) {
      state.user = { ...state.user, ...action.payload } as AppNS.User;
    },
    updateUserCompany(state: AuthState, action: { payload: AppNS.Params }) {
      if (state.user) {
        state.user.company = {
          ...state.user.company,
          ...action.payload.model.properties,
          avatar: action.payload.model.avatar,
          companyName: action.payload.model.properties.name,
        };
      }
    },
    logoutStart(state: AuthState) {
      state.isLoggingOut = true;
    },
    logout(state: AuthState, action: { payload: boolean | undefined }) {
      cleanLocalStorage();
      state.user = null;
      signOut(action?.payload);
    },
    getProjectManagersStart(state: AuthState) {
      state.isLoadingProjectManagers = true;
    },
    getProjectManagersSuccess(
      state: AuthState,
      action: PayloadAction<Array<AppNS.User>>
    ) {
      state.isLoadingProjectManagers = false;
      state.projectManagers = action.payload;
    },
    setCurrentApp(state: AuthState, action: { payload: string }) {
      state.currentApp = action.payload;
      localStorage.setItem('currentApp', action.payload);
    },
  },
});

export const {
  changeOnboardingStepFailed,
  changeOnboardingStepStart,
  changeOnboardingStepSuccess,
  logout,
  logoutStart,
  setAddressFailed,
  setAddressStart,
  setAddressSuccess,
  setAuthData,
  setAvailabilityFailed,
  setAvailabilityStart,
  setAvailabilitySuccess,
  setAvatarFailed,
  setAvatarStart,
  setAvatarSuccess,
  setCompanyAvatarSuccess,
  setVideoFailed,
  setVideoStart,
  setVideoSuccess,
  setCurrentServiceAddressIdFailed,
  setCurrentServiceAddressIdStart,
  setCurrentServiceAddressIdSuccess,
  setCurrentDistanceFailed,
  setCurrentDistanceStart,
  setCurrentDistanceSuccess,
  setNeedsFailed,
  setNeedsStart,
  setNeedsSuccess,
  setUnavailablePeriodsFailed,
  setUnavailablePeriodsStart,
  setUnavailablePeriodsSuccess,
  setPersonalDataFailed,
  setPersonalDataStart,
  setPersonalDataSuccess,
  updateUserData,
  updateUserCompany,
  setDescriptionDataFailed,
  setDescriptionDataStart,
  setDescriptionDataSuccess,
  changeEmailStart,
  changeEmailFailed,
  changeEmailSuccess,
  changeMobileStart,
  changeMobileFailed,
  changeMobileSuccess,
  setPublicDetailsDataStart,
  setPublicDetailsDataFailed,
  setPublicDetailsDataSuccess,
  setOfficeContactDataStart,
  setOfficeContactDataFailed,
  setOfficeContactDataSuccess,
  setProposalPricingStart,
  setProposalPricingFailed,
  setProposalPricingSuccess,
  setFixedPricingStart,
  setFixedPricingFailed,
  setFixedPricingSuccess,
  getProjectManagersStart,
  getProjectManagersSuccess,
  setCurrentApp,
} = authSlice.actions;

export default authSlice.reducer;

export const getProjectManagers =
  (agencyCompanyId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getProjectManagersStart());
      const response = await apiGetProjectManagers(agencyCompanyId);

      dispatch(getProjectManagersSuccess(response));
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err.toString());
      dispatch(getProjectManagersSuccess([]));
    }
  };
